import { Directive, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReflectionService } from '../../shared/utilities/reflection.service';
import { Hint, HintDefinition, InputType, InputValueChange, TextAreaInputType, TextInputType } from '../form.types';
import { QuestionStateService } from '../question-state.service';

@Directive()
export abstract class BaseInputComponent implements OnDestroy {
    @Output() inputValueChangeEventEmitter: EventEmitter<InputValueChange> = new EventEmitter<InputValueChange>();
    @Output() inputIdEvent: EventEmitter<string> = new EventEmitter<string>();
    input: InputType;
    wrapperClasses: string;
    hint: Hint;
    errorMessage: string;
    editPromptSuffix: string = '-edit-prompt';
    private inputValueChangeSubscription: Subscription;
    private dependantValueChangesSubscription: Subscription;
    protected reflection: ReflectionService = new ReflectionService();
    private questionStateSubscription$: Subscription;

    ngOnDestroy() {
        if (this.inputValueChangeSubscription) {
            this.inputValueChangeSubscription.unsubscribe();
        }
        if (this.dependantValueChangesSubscription) {
            this.dependantValueChangesSubscription.unsubscribe();
        }
        if (this.questionStateSubscription$) {
            this.questionStateSubscription$.unsubscribe();
        }
    }

    private questionStateListener(questionStateService: QuestionStateService) {
        this.questionStateSubscription$ = questionStateService.inputStateObservable.subscribe((next) => {
            if (
                next &&
                this.input?.inputStateListener?.alias &&
                next.input.inputStateListener.alias === this.input.inputStateListener.alias
            ) {
                if (next.updates) {
                    this.input = this.reflection.updateProperties(this.input, next.updates);
                }
            }
        });
    }

    initialize(input: InputType, questionStateService: QuestionStateService): void {
        this.input = input;
        const c = this.reflection.collectArray(this?.input?.classes);
        this.wrapperClasses = c && c.length ? c.join(' ') : 'full-width';
        this.setDefaults();
        this.addRequiredMessage();
        this.listenToControlChanges(input.discriminator !== 'SELECT');
        this.checkForHints();
        if (this.input.inputStateListener) {
            this.questionStateListener(questionStateService);
        }
    }

    setDefaults(): void {
        this.input.appearance = this.input.appearance ? this.input.appearance : 'fill';
        this.input.floatLabel = this.input.floatLabel ? this.input.floatLabel : 'auto';
        this.input.required = this.input.required ? this.input.required : false;
        if (this.reflection.isNullOrEmpty(this.input.label)) {
            this.input.label = this.reflection.toTitleCase(this.input.property.split('.').join(' '));
        }
    }

    listenToControlChanges(emitChanges: boolean) {
        if (this.input.control) {
            this.inputValueChangeSubscription = this.input.control.valueChanges.subscribe((value) => {
                if (emitChanges) {
                    this.inputValueChangeEventEmitter.emit({
                        input: this.input,
                        payload: value
                    } as InputValueChange);
                }
                if (this.input.updateValidityOnChange) {
                    this.input.updateValidityOnChange.updateValueAndValidity();
                    this.input.updateValidityOnChange.markAsTouched();
                }
                this.checkForErrors();
                this.checkForHints();
            });
        }
    }

    checkForHints(): void {
        this.hint = undefined;
        if (this.input.hints && this.input.hints.length) {
            for (const index in this.input.hints) {
                const hint: HintDefinition = this.input.hints[index];
                if (!hint.callback || hint.callback.function.call(hint.callback.this)) {
                    this.hint = {
                        message: hint.message,
                        class: hint.class
                    };
                    break;
                }
            }
        }
        if (!this.hint && !this.input.noHint) {
            switch (this.input.discriminator) {
                case 'DATE': {
                    this.hint = {
                        message: 'Please use the format mm/dd/yyyy'
                    };
                    break;
                }
                case 'TEXT': {
                    if ((this.input as TextInputType).maxChars) {
                        this.hint = {
                            message: `Max ${(this.input as TextInputType).maxChars} characters`
                        };
                    }
                    break;
                }
                case 'TEXTAREA': {
                    if ((this.input as TextAreaInputType).maxChars) {
                        this.hint = {
                            message: `Max ${(this.input as TextAreaInputType).maxChars} characters`
                        };
                    }
                    break;
                }
            }
        }
    }

    addRequiredMessage() {
        this.input.errorTypes = this.input.errorTypes ? this.input.errorTypes : [];
        if (this.input.required && !this.input.errorTypes.find((x) => x.code === 'required')) {
            let messageLabel = this.input.label.trim();
            if (messageLabel.endsWith('.')) {
                messageLabel = messageLabel.substring(0, messageLabel.length - 1);
            }
            this.input.errorTypes.push({
                code: 'required',
                message: `${messageLabel} is required.`
            });
        }
    }

    private getIndexOfError(): number {
        if (this.input.errorTypes && this.input.errorTypes.length) {
            for (let i = 0; i < this.input.errorTypes.length; ++i) {
                if (this.input.control.hasError(this.input.errorTypes[i].code)) {
                    return i;
                }
            }
        }
        return -1;
    }

    hasError(): boolean {
        const index = this.getIndexOfError();
        if (index >= 0) {
            return true;
        }
        return false;
    }

    checkForErrors() {
        const index = this.getIndexOfError();
        if (index >= 0) {
            this.errorMessage = this.input.errorTypes[index].message;
            return;
        }
        this.errorMessage = '';
    }

    blurEvent($event: FocusEvent) {
        this.checkForErrors();
        if (this.input.blurCallBack) {
            this.input.blurCallBack.function.call(this.input.blurCallBack.this, $event);
        }
    }

    focusEvent($event: FocusEvent) {
        if (this.input.focusCallBack) {
            this.input.focusCallBack.function.call(this.input.focusCallBack.this, $event);
        }
    }

    hasAnyValue(): boolean {
        return this.input.control && this.reflection.isNotNullOrEmpty(this.input.control.value);
    }
}
